import { useMemo } from 'react';
import {
  getModelCapability,
  getUserSelectableModels,
  type Provider,
} from '@inference.engine/model-registry/web';
import { useCurrentAgent } from '@/features/chats/stores';
import { getModelDisplayLabel } from '@/features/chats/lib/getModelDisplayLabel';

/* eslint-disable i18next/no-literal-string -- vendor names */
const PROVIDER_LABELS: Record<Provider, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  google: 'Google',
};
/* eslint-enable i18next/no-literal-string */

export interface AgentModelOption {
  value: string;
  label: string;
  secondaryLabel: string;
}

/** Only for the moment before the agent's offered models have loaded. */
export const DEFAULT_SELECTABLE_MODEL = getUserSelectableModels()[0].key;

export function toAgentModelOption(key: string): AgentModelOption {
  const capability = getModelCapability(key);
  return {
    value: key,
    label: getModelDisplayLabel(key),
    secondaryLabel: capability ? PROVIDER_LABELS[capability.provider] : '',
  };
}

/**
 * The models the current agent offers, in its order, labelled from the registry. The backend fills
 * `enabledModels` from the curated list and the workspace's allowed providers, so a picker built
 * from it can neither offer a model the agent refuses nor miss one it accepts.
 */
export const useAgentModelOptions = (): AgentModelOption[] => {
  const agent = useCurrentAgent();
  return useMemo(
    () => (agent?.enabledModels ?? []).map(toAgentModelOption),
    [agent?.enabledModels],
  );
};
